// In src/pages/Chat.js

import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
import {
  collection, query, orderBy, onSnapshot, addDoc, serverTimestamp,
  doc, updateDoc, getDoc
} from 'firebase/firestore';
import { markChatAsRead } from '../services/chatService'; // <-- Import the new service

// Import Material-UI components
import { Box, TextField, Button, Paper, Typography, List, ListItem, ListItemText, CircularProgress, Avatar } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';

function Chat() {
  const { chatId } = useParams();
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [chatPartner, setChatPartner] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef(null);

  // Auto-scroll to the latest message
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(scrollToBottom, [messages]);

  // Fetch info and messages, and mark chat as read
  useEffect(() => {
    if (!chatId || !currentUser) return;

    // --- Mark this chat as read for the current user ---
    markChatAsRead(chatId, currentUser.uid);

    // Fetch chat partner info
    const chatDocRef = doc(db, 'chats', chatId);
    getDoc(chatDocRef).then(docSnap => {
      if (docSnap.exists()) {
        const chatData = docSnap.data();
        const partnerId = chatData.memberIds.find(id => id !== currentUser.uid);
        if (partnerId && chatData.memberInfo[partnerId]) {
          setChatPartner(chatData.memberInfo[partnerId]);
        }
      }
    });

    // Fetch messages
    const messagesColRef = collection(db, 'chats', chatId, 'messages');
    const q = query(messagesColRef, orderBy('sentAt', 'asc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedMessages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setMessages(fetchedMessages);
    });

    return () => unsubscribe();
  }, [chatId, currentUser]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (newMessage.trim() === '' || !chatId || isSending) return;

    setIsSending(true);
    const messagesColRef = collection(db, 'chats', chatId, 'messages');
    const chatDocRef = doc(db, 'chats', chatId);

    try {
      await addDoc(messagesColRef, {
        text: newMessage,
        senderId: currentUser.uid,
        sentAt: serverTimestamp(),
      });

      await updateDoc(chatDocRef, {
        lastMessageText: newMessage,
        lastMessageAt: serverTimestamp(),
      });

      setNewMessage('');
    } catch (error) {
      console.error("Error sending message:", error);
      alert("Failed to send message.");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 120px)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', p: 2, borderBottom: '1px solid #ddd' }}>
        {chatPartner && <Avatar sx={{ mr: 2 }}>{chatPartner.firstName.charAt(0)}</Avatar>}
        <Typography variant="h5">
          {chatPartner ? `Chat with ${chatPartner.firstName} ${chatPartner.lastName}` : 'Chat'}
        </Typography>
      </Box>
      <Paper
        elevation={0}
        sx={{ flexGrow: 1, overflow: 'auto', p: 2, mb: 2 }}
      >
        <List>
          {messages.map((msg) => (
            <ListItem
              key={msg.id}
              sx={{
                display: 'flex',
                justifyContent: msg.senderId === currentUser.uid ? 'flex-end' : 'flex-start',
              }}
            >
              <Box
                sx={{
                  bgcolor: msg.senderId === currentUser.uid ? 'primary.main' : 'grey.200',
                  color: msg.senderId === currentUser.uid ? 'primary.contrastText' : 'black',
                  p: '8px 12px',
                  borderRadius: '18px',
                  maxWidth: '70%',
                }}
              >
                <ListItemText primary={msg.text} />
              </Box>
            </ListItem>
          ))}
          <div ref={messagesEndRef} />
        </List>
      </Paper>
      <Box component="form" onSubmit={handleSendMessage} sx={{ display: 'flex', p: 1 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Type a message..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          autoComplete="off"
        />
        <Button type="submit" variant="contained" disabled={isSending} sx={{ ml: 1, p: '10px' }}>
          {isSending ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
        </Button>
      </Box>
    </Box>
  );
}

export default Chat;

